import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Stack,
  Button,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
} from "@mui/material";
import AccountCircle from "@mui/icons-material/AccountCircle";
import { Link as RouterLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext.tsx";
import DayAndNightSwitch from "./extras/DayAndNightSwitch.tsx";

interface LayoutProps {
  children: React.ReactNode;
  onToggleDarkMode: () => void;
  darkMode: boolean;
}

const navButtonSx = {
  fontFamily: "Montserrat",
  fontWeight: 600,
  fontSize: "0.8125rem",
  letterSpacing: "0.02em",
  px: 1.25,
  py: 0.5,
  minWidth: "auto",
  borderRadius: 1.5,
  opacity: 0.92,
  "&:hover": { opacity: 1, bgcolor: "rgba(255,255,255,0.08)" },
} as const;

const compactNavItems = [
  { to: "/", label: "Home" },
  { to: "/parent", label: "Parents" },
  { to: "/report", label: "Reports" },
  { to: "/dokk-monitor", label: "Dokk Monitor" },
] as const;

const Layout: React.FC<LayoutProps> = ({ children, onToggleDarkMode, darkMode }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { isAuthenticated, logout } = useAuth();
  const theme = useTheme();
  const showInlineNav = useMediaQuery(theme.breakpoints.up("md"));
  const menuOpen = Boolean(anchorEl);
  const menuId = "account-menu";

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    closeMenu();
    logout();
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: darkMode ? "#121212" : "#020042",
          borderBottom: "1px solid",
          borderColor: darkMode ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.12)",
          backdropFilter: "saturate(180%) blur(8px)",
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            px: { xs: 1.5, sm: 2, md: 3 },
            minHeight: { xs: 56, sm: 64 },
            maxWidth: "100%",
            flexWrap: "nowrap",
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: 0,
              flex: showInlineNav && isAuthenticated ? "1 1 auto" : "1 1 0%",
              cursor: "pointer",
              color: "inherit",
              textDecoration: "none",
              "&:visited": { color: "inherit" },
              "&:hover": { textDecoration: "none", opacity: 0.92 },
            }}
            component={RouterLink}
            to="/"
            aria-label="IT Master home"
          >
            <Box
              component="img"
              src="/logos/IT logo green.png"
              alt=""
              sx={{
                height: { xs: 32, sm: 38 },
                width: "auto",
                mr: { xs: 1, sm: 1.25 },
                flexShrink: 0,
              }}
            />
            <Typography
              variant="h6"
              component="span"
              noWrap
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 700,
                fontSize: { xs: "1.05rem", sm: "1.2rem", md: "1.35rem" },
                letterSpacing: "-0.02em",
                lineHeight: 1.2,
                color: "inherit",
              }}
            >
              IT Master
            </Typography>
          </Box>

          {isAuthenticated && showInlineNav && (
            <Stack
              direction="row"
              spacing={0.25}
              alignItems="center"
              sx={{ flexShrink: 0, ml: { md: 1 } }}
            >
              <Button color="inherit" component={RouterLink} to="/" sx={navButtonSx}>
                Home
              </Button>
              <Button color="inherit" component={RouterLink} to="/parent" sx={navButtonSx}>
                Parents
              </Button>
              <Button color="inherit" component={RouterLink} to="/report" sx={navButtonSx}>
                Reports
              </Button>
              <Button color="inherit" component={RouterLink} to="/dokk-monitor" sx={navButtonSx}>
                Dokk Monitor
              </Button>
            </Stack>
          )}

          {isAuthenticated && (
            <>
              <IconButton
                id="account-menu-button"
                edge="end"
                color="inherit"
                aria-label="Account menu"
                aria-controls={menuOpen ? menuId : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? "true" : undefined}
                onClick={openMenu}
                sx={{ flexShrink: 0, ml: showInlineNav ? 0.5 : "auto" }}
              >
                <AccountCircle sx={{ fontSize: { xs: 28, sm: 30 } }} />
              </IconButton>
              <Menu
                id={menuId}
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={closeMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                MenuListProps={{ "aria-labelledby": "account-menu-button" }}
              >
                {!showInlineNav &&
                  compactNavItems.map(({ to, label }) => (
                    <MenuItem
                      key={to}
                      component={RouterLink}
                      to={to}
                      onClick={closeMenu}
                    >
                      {label}
                    </MenuItem>
                  ))}
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
                <Box
                  sx={{ px: 2, py: 1.5 }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                    Dark mode
                  </Typography>
                  <DayAndNightSwitch checked={darkMode} onChange={onToggleDarkMode} />
                </Box>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          px: { xs: 1, sm: 1.5, md: 2 },
          py: { xs: 1.5, sm: 2 },
          mt: { xs: "56px", sm: "64px" },
          width: "100%",
          maxWidth: "100vw",
          boxSizing: "border-box",
        }}
      >
        {children}
      </Box>
    </>
  );
};

export default Layout;
